import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Avatar,
} from "@mui/material";
import {
  Download,
  PictureAsPdf,
  VideoLibrary,
  Image,
  InsertDriveFile,
  Folder,
  Visibility,
} from "@mui/icons-material";
import { useMaterials } from "../../hooks/queries/useAssignments";
import SearchBar from "../../components/navigation/SearchBar";
import ViewDialog from "../../components/forms/ViewDialog";
import EmptyState from "../../components/data-display/EmptyState";

const Materials = () => {
  const { data: materialsData, isLoading: loading } = useMaterials({});
  const materials = materialsData?.materials || materialsData || [];
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("all");
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  // Sample data for demo
  const sampleMaterials = [
    {
      id: 1,
      title: "Mathematics - Chapter 5 Notes",
      subject: "Mathematics",
      teacher: "Dr. Smith",
      type: "pdf",
      size: "2.5 MB",
      uploadedAt: "2025-12-15",
      description: "Complete notes for Chapter 5: Quadratic Equations",
    },
    {
      id: 2,
      title: "Physics Lab Guidelines",
      subject: "Physics",
      teacher: "Prof. Johnson",
      type: "pdf",
      size: "1.2 MB",
      uploadedAt: "2025-12-14",
      description: "Guidelines for conducting physics lab experiments",
    },
    {
      id: 3,
      title: "Chemistry Video Lecture",
      subject: "Chemistry",
      teacher: "Dr. Wilson",
      type: "video",
      size: "150 MB",
      uploadedAt: "2025-12-13",
      description: "Video lecture on Chemical Reactions",
    },
    {
      id: 4,
      title: "English Grammar Workbook",
      subject: "English",
      teacher: "Ms. Davis",
      type: "pdf",
      size: "3.8 MB",
      uploadedAt: "2025-12-12",
      description: "Comprehensive grammar exercises",
    },
    {
      id: 5,
      title: "Biology Diagrams",
      subject: "Biology",
      teacher: "Dr. Brown",
      type: "image",
      size: "5.2 MB",
      uploadedAt: "2025-12-10",
      description: "Labeled diagrams of human anatomy",
    },
    {
      id: 6,
      title: "History Timeline Presentation",
      subject: "History",
      teacher: "Mr. Taylor",
      type: "file",
      size: "8.5 MB",
      uploadedAt: "2025-12-08",
      description: "Interactive timeline of World War II",
    },
  ];

  const displayMaterials = materials.length > 0 ? materials : sampleMaterials;
  const subjects = ["all", ...new Set(displayMaterials.map((m) => m.subject))];

  const getFileIcon = (type) => {
    switch (type) {
      case "pdf":
        return <PictureAsPdf sx={{ color: "#e53935" }} />;
      case "video":
        return <VideoLibrary sx={{ color: "#1976d2" }} />;
      case "image":
        return <Image sx={{ color: "#43a047" }} />;
      default:
        return <InsertDriveFile sx={{ color: "#757575" }} />;
    }
  };

  const filteredMaterials = displayMaterials.filter((material) => {
    const matchesSearch =
      material.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      material.subject.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSubject =
      selectedSubject === "all" || material.subject === selectedSubject;
    return matchesSearch && matchesSubject;
  });

  const handleDownload = (material) => {
    // Implement download logic
    console.log("Downloading:", material.title);
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Study Materials
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Access your course materials and resources
          </Typography>
        </Box>
      </Box>

      {/* Search and Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search materials..."
            filters={[
              {
                name: "subject",
                label: "Subject",
                value: selectedSubject,
                onChange: setSelectedSubject,
                options: subjects.map((s) => ({
                  value: s,
                  label: s === "all" ? "All Subjects" : s,
                })),
              },
            ]}
          />
        </CardContent>
      </Card>

      {/* Materials Grid */}
      <Grid container spacing={3}>
        {filteredMaterials.map((material) => (
          <Grid item xs={12} sm={6} md={4} key={material.id}>
            <Card
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "grey.100",
                      width: 48,
                      height: 48,
                    }}
                  >
                    {getFileIcon(material.type)}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} noWrap>
                      {material.title}
                    </Typography>
                    <Chip
                      label={material.subject}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {material.description}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    By {material.teacher}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {material.size}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.disabled">
                  Uploaded: {material.uploadedAt}
                </Typography>
              </CardContent>

              <Box sx={{ p: 2, pt: 0, display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Visibility />}
                  onClick={() => {
                    setSelectedMaterial(material);
                    setViewDialogOpen(true);
                  }}
                  sx={{ flex: 1 }}
                >
                  View
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Download />}
                  onClick={() => handleDownload(material)}
                  sx={{ flex: 1 }}
                >
                  Download
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}

        {filteredMaterials.length === 0 && (
          <Grid item xs={12}>
            <EmptyState
              icon={<Folder sx={{ fontSize: 64, color: "grey.300" }} />}
              title="No materials found"
              message="Try adjusting your search or filter criteria"
            />
          </Grid>
        )}
      </Grid>

      {/* View Dialog */}
      <ViewDialog
        open={viewDialogOpen}
        title="Material Details"
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fields={
          selectedMaterial
            ? [
                {
                  label: "Title",
                  value: (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: "grey.100",
                          width: 48,
                          height: 48,
                        }}
                      >
                        {getFileIcon(selectedMaterial.type)}
                      </Avatar>
                      <Box>
                        <Typography variant="body1" fontWeight={600}>
                          {selectedMaterial.title}
                        </Typography>
                        <Chip
                          label={selectedMaterial.subject}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </Box>
                  ),
                  span: 12,
                },
                { label: "Teacher", value: selectedMaterial.teacher },
                { label: "File Size", value: selectedMaterial.size },
                {
                  label: "Type",
                  value: selectedMaterial.type.toUpperCase(),
                },
                { label: "Uploaded On", value: selectedMaterial.uploadedAt },
                {
                  label: "Description",
                  value: selectedMaterial.description,
                  span: 12,
                },
              ]
            : []
        }
        downloadLabel="Download"
        onDownload={
          selectedMaterial
            ? () => handleDownload(selectedMaterial)
            : undefined
        }
      />
    </Box>
  );
};

export default Materials;
